import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button } from '../../../components/ui';
import { Store, CheckSquare, Square } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { useAuth } from '../../../app/auth';

interface LocalResumenItem {
  local_id: string;
  nombre_local: string;
  local_activo: boolean;
}

interface SorteoItem {
  id: string;
  nombre: string;
  tipo_id: string;
  estado: string;
  eliminado?: boolean;
}

interface AsignacionResponse {
  local_id: string;
  sorteo_ids: string[];
}

export const BancaLocalesSorteosPage: React.FC = () => {
  const { user } = useAuth();
  const soloLectura = user?.role !== 'BANCA';
  const [locales, setLocales] = useState<LocalResumenItem[]>([]);
  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState<LocalResumenItem | null>(null);
  const [asignados, setAsignados] = useState<Set<string>>(new Set());
  const [pendiente, setPendiente] = useState<Set<string>>(new Set());
  const [cargandoAsignacion, setCargandoAsignacion] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [hayPendientes, setHayPendientes] = useState(false);

  // Cargar locales y sorteos en paralelo
  useEffect(() => {
    const cargarDatos = async () => {
      setLoading(true);
      setError(null);
      try {
        const [localesData, sorteosData] = await Promise.all([
          fetchAPI<LocalResumenItem[]>('/usuarios/banca/locales/resumen-vendedores'),
          fetchAPI<SorteoItem[]>('/sorteos'),
        ]);
        setLocales(localesData);
        setSorteos(sorteosData.filter((s) => !s.eliminado));
      } catch {
        setError('Error al cargar datos. Intente de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    cargarDatos();
  }, []);

  // Cargar asignaciones del local seleccionado
  const cargarAsignacion = useCallback(async (local: LocalResumenItem) => {
    setCargandoAsignacion(true);
    setError(null);
    setMensaje(null);
    try {
      const data = await fetchAPI<AsignacionResponse>(`/locales/${local.local_id}/sorteos`);
      const ids = new Set(data.sorteo_ids);
      setAsignados(ids);
      setPendiente(new Set(ids));
      setHayPendientes(false);
    } catch {
      setError('Error al cargar asignaciones del local.');
      setAsignados(new Set());
      setPendiente(new Set());
    } finally {
      setCargandoAsignacion(false);
    }
  }, []);

  const seleccionarLocal = (local: LocalResumenItem) => {
    setLocalSeleccionado(local);
    cargarAsignacion(local);
  };

  const toggleSorteo = (sorteoId: string) => {
    setPendiente((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(sorteoId)) {
        siguiente.delete(sorteoId);
      } else {
        siguiente.add(sorteoId);
      }
      return siguiente;
    });
    setHayPendientes(true);
    setMensaje(null);
  };

  const guardar = async () => {
    if (!localSeleccionado) return;
    setGuardando(true);
    setError(null);
    setMensaje(null);
    try {
      await fetchAPI(`/locales/${localSeleccionado.local_id}/sorteos`, {
        method: 'POST',
        body: JSON.stringify({ sorteo_ids: Array.from(pendiente) }),
      });
      setAsignados(new Set(pendiente));
      setHayPendientes(false);
      setMensaje('Asignación guardada correctamente.');
    } catch {
      setError('Error al guardar la asignación. Intente de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => {
    setPendiente(new Set(asignados));
    setHayPendientes(false);
    setMensaje(null);
  };

  const seleccionarTodos = () => {
    setPendiente(new Set(sorteos.map((s) => s.id)));
    setHayPendientes(true);
    setMensaje(null);
  };

  const deseleccionarTodos = () => {
    setPendiente(new Set());
    setHayPendientes(true);
    setMensaje(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-gray-500">Cargando...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Store size={22} className="text-gray-700" />
        <h1 className="text-xl font-semibold text-gray-800">Sorteos por Local</h1>
      </div>
      <p className="text-sm text-gray-500">
        Seleccione un local para ver y editar qué sorteos puede vender. Si un local no tiene asignaciones, hereda todos los sorteos de la banca.
      </p>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {mensaje && (
        <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700">
          {mensaje}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Panel de locales */}
        <Card className="md:col-span-1">
          <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Locales ({locales.length})
            </h2>
            {locales.length === 0 ? (
              <p className="text-sm text-gray-400">No hay locales activos.</p>
            ) : (
              <ul className="space-y-1">
                {locales.map((local) => (
                  <li key={local.local_id}>
                    <button
                      onClick={() => seleccionarLocal(local)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        localSeleccionado?.local_id === local.local_id
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="block truncate">{local.nombre_local}</span>
                      {!local.local_activo && (
                        <span className="text-xs text-gray-400">Inactivo</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* Panel de sorteos */}
        <Card className="md:col-span-2">
          <div className="p-4">
            {!localSeleccionado ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <Store size={32} className="mb-2 opacity-40" />
                <p className="text-sm">Seleccione un local para gestionar sus sorteos.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {localSeleccionado.nombre_local}
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {pendiente.size === 0
                        ? 'Hereda todos los sorteos (sin asignación específica)'
                        : `${pendiente.size} de ${sorteos.length} sorteos asignados`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={seleccionarTodos}
                      className="text-xs text-blue-600 hover:underline"
                      disabled={cargandoAsignacion || guardando || soloLectura}
                    >
                      Todos
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={deseleccionarTodos}
                      className="text-xs text-gray-500 hover:underline"
                      disabled={cargandoAsignacion || guardando || soloLectura}
                    >
                      Ninguno
                    </button>
                  </div>
                </div>

                {cargandoAsignacion ? (
                  <p className="text-sm text-gray-400 py-4">Cargando asignaciones...</p>
                ) : sorteos.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4">No hay sorteos disponibles.</p>
                ) : (
                  <ul className="space-y-1 max-h-96 overflow-y-auto">
                    {sorteos.map((sorteo) => {
                      const marcado = pendiente.has(sorteo.id);
                      return (
                        <li key={sorteo.id}>
                          <button
                            onClick={() => toggleSorteo(sorteo.id)}
                            disabled={guardando || soloLectura}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors text-left"
                          >
                            {marcado ? (
                              <CheckSquare size={18} className="text-blue-600 flex-shrink-0" />
                            ) : (
                              <Square size={18} className="text-gray-300 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <span className={`block text-sm truncate ${marcado ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                                {sorteo.nombre}
                              </span>
                              <span className="text-xs text-gray-400">{sorteo.tipo_id}</span>
                            </div>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                sorteo.estado === 'ABIERTO'
                                  ? 'bg-green-100 text-green-700'
                                  : sorteo.estado === 'INACTIVO'
                                  ? 'bg-gray-100 text-gray-500'
                                  : 'bg-red-100 text-red-600'
                              }`}
                            >
                              {sorteo.estado}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {hayPendientes && !cargandoAsignacion && !soloLectura && (
                  <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={descartar}
                      disabled={guardando}
                    >
                      Descartar
                    </Button>
                    <Button
                      size="sm"
                      onClick={guardar}
                      disabled={guardando}
                    >
                      {guardando ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
